import { useRef } from 'preact/hooks';
import { eventSpans, totalHours, type TimelineEvent } from '../sim/timeline.ts';
import {
  conditions,
  displayEvent,
  displayHour,
  scenarioStatus,
  selectedTimelineEventId,
  uiMode,
  viewHour,
} from '../state/store.ts';
import { realDataStart } from '../state/realData.ts';
import { formatEventSummary, formatTimelineClock, formatTimelineTick } from './format.ts';

const DAY_HOURS = 24;

/** Hours from `origin` to the next local midnight (0 if it is one). */
function hoursToLocalMidnight(origin: Date): number {
  const next = new Date(origin);
  next.setHours(24, 0, 0, 0);
  const h = Math.round((next.getTime() - origin.getTime()) / 3_600_000);
  return h >= DAY_HOURS ? 0 : h;
}

/** Background colour class for one event's block, by kind — storms with a rain-ish valley
 * temperature (≥ ~1 °C) get a striped tint so a rain-on-snow storm reads differently from a cold
 * powder one, and fair weather with the valley above freezing (melt) gets a warm band. */
function blockClass(e: TimelineEvent): string {
  if (e.kind === 'storm') {
    const rainy = e.temperature.lowTemp >= 1;
    return 'timeline-block' + (rainy ? ' timeline-block-storm-rain' : ' timeline-block-storm');
  }
  return (
    'timeline-block ' +
    (e.clearSky ? 'timeline-block-fair-clear' : 'timeline-block-fair-cloudy') +
    (e.temperature.lowTemp > 0 ? ' timeline-block-thaw' : '')
  );
}

/**
 * Timeline bar docked at the bottom of the 3D scene: coloured blocks per weather event, day
 * ticks, an evaluation-hour marker and a draggable/keyboard-operable scrubber. Dragging previews
 * the recorded snapshot at that hour (`viewHour`); releasing (or a keyboard step) commits it as
 * the new evaluation hour, which the store re-runs exactly. Shown in both simple and advanced
 * mode; clicking a block in advanced mode also selects that event in the timeline editor.
 */
export function TimelineBar() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const events = conditions.value.events;
  const total = totalHours(events);
  const spans = eventSpans(events);
  const evalHour = conditions.value.evaluationHour;
  const hour = Math.min(total, Math.max(0, displayHour.value));
  const event = displayEvent.value;
  const computing = viewHour.value !== null && scenarioStatus.value.kind === 'running';
  const advanced = uiMode.value === 'advanced';
  const selectedId = selectedTimelineEventId.value;

  const pct = (h: number): number => (total > 0 ? (h / total) * 100 : 0);

  // Imported conditions run on real time: label dates, tick at local midnight.
  const origin = realDataStart();
  // Daily ticks (every other day on long timelines), plus the end; a tick too close to the end
  // is dropped so the labels don't collide.
  const tickStep = total > 10 * DAY_HOURS ? 2 * DAY_HOURS : DAY_HOURS;
  const ticks: number[] = [];
  const firstTick = origin ? hoursToLocalMidnight(origin) : 0;
  for (let h = firstTick; h < total - 0.08 * total; h += tickStep) ticks.push(h);
  ticks.push(total);

  function hourFromClientX(clientX: number): number {
    const el = trackRef.current;
    if (!el || total <= 0) return 0;
    const rect = el.getBoundingClientRect();
    const frac = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return Math.round(Math.min(1, Math.max(0, frac)) * total);
  }

  function commit(h: number): void {
    const clamped = Math.min(total, Math.max(0, h));
    viewHour.value = clamped;
    conditions.value = { ...conditions.value, evaluationHour: clamped };
  }

  function onPointerDown(e: PointerEvent): void {
    draggingRef.current = true;
    trackRef.current?.setPointerCapture(e.pointerId);
    viewHour.value = hourFromClientX(e.clientX);
  }

  function onPointerMove(e: PointerEvent): void {
    if (!draggingRef.current) return;
    viewHour.value = hourFromClientX(e.clientX);
  }

  function onPointerUp(e: PointerEvent): void {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    commit(hourFromClientX(e.clientX));
  }

  function onKeyDown(e: KeyboardEvent): void {
    const step = e.shiftKey ? 12 : 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      commit(hour - step);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      commit(hour + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      commit(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      commit(total);
    }
  }

  return (
    <div class="timeline-bar">
      <div
        class="timeline-track"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div class="timeline-blocks">
          {spans.map((span, i) => {
            const e = events[i]!;
            return (
              <div
                key={span.id}
                class={
                  blockClass(e) +
                  (advanced && selectedId === span.id ? ' timeline-block-selected' : '')
                }
                style={{
                  left: `${pct(span.start)}%`,
                  width: `${Math.max(0, pct(span.end - span.start))}%`,
                }}
                title={formatEventSummary(e)}
                onClick={() => {
                  if (advanced) selectedTimelineEventId.value = span.id;
                }}
              />
            );
          })}
        </div>
        <div class="timeline-ticks">
          {ticks.map((h) => (
            <span
              key={h}
              class={h === total ? 'timeline-tick timeline-tick-end' : 'timeline-tick'}
              style={{ left: `${pct(h)}%` }}
            >
              <span class="timeline-tick-label">
                {h === total && origin
                  ? formatTimelineClock(h, origin)
                  : formatTimelineTick(h, origin)}
              </span>
            </span>
          ))}
        </div>
        <div
          class="timeline-eval-marker"
          style={{ left: `${pct(evalHour)}%` }}
          title="Evaluated at"
        />
        <div
          class="timeline-scrubber"
          role="slider"
          tabIndex={0}
          aria-label="Timeline hour"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={hour}
          aria-valuetext={
            event
              ? `${formatTimelineClock(hour, origin)} — ${formatEventSummary(event)}`
              : formatTimelineClock(hour, origin)
          }
          style={{ left: `${pct(hour)}%` }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div class="timeline-status-row">
        <span class="timeline-hour-label">{formatTimelineClock(hour, origin)}</span>
        <span class="timeline-event-label">
          {event ? formatEventSummary(event) : 'No weather timeline'}
        </span>
        {computing ? <span class="timeline-computing">Computing…</span> : null}
      </div>
    </div>
  );
}
